import { trackError } from '@billy/track-error'
import React, {
  createContext,
  Dispatch,
  ReactNode,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { pdfjs } from 'react-pdf'

import { FileType, PreviewFile } from '../types'
import { getFileType } from '../utils/getFileType'

interface ContextState {
  isLoading: boolean
  filesPages: Map<string, number>
  setIsLoading: Dispatch<SetStateAction<boolean>>
  totalPageCount: number
}

const PdfPreloadContext = createContext<ContextState | undefined>(undefined)

interface PdfPreloadContextProviderProps {
  files: PreviewFile[]
  asType?: FileType
  children: ReactNode
}

export const PdfPreloadContextProvider = ({ files, asType, children }: PdfPreloadContextProviderProps) => {
  const [filesPages, setFilesPages] = useState<Map<string, number>>(new Map())
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [totalPageCount, setTotalPageCount] = useState<number>(0)

  const getPdfMetadata = useCallback(async (fileSrc: string) => {
    try {
      const pdf = await pdfjs.getDocument(fileSrc).promise
      return pdf.numPages
    } catch (error) {
      trackError(error)
      return 0
    }
  }, [])

  useEffect(() => {
    const getAllPagesData = async () => {
      const filesPagesCount = new Map()
      let total = 0

      for (const file of files) {
        const fileId = file.id || file.src
        const fileType = asType ?? getFileType(file.src)

        if (fileType === FileType.Image) {
          filesPagesCount.set(fileId, 1)
        } else {
          const pages = await getPdfMetadata(file.src)
          filesPagesCount.set(fileId, pages)
        }
      }

      const sortedFilesPages = new Map(
        files.map((file) => {
          const fileId = file.id || file.src
          const size = filesPagesCount.get(fileId)
          total += size
          return [fileId, size]
        }),
      )

      setFilesPages(sortedFilesPages)
      setTotalPageCount(total)
      setIsLoading(false)
    }

    setIsLoading(true)
    getAllPagesData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [files, asType])

  return (
    <PdfPreloadContext.Provider value={{ isLoading, filesPages, totalPageCount, setIsLoading }}>
      {children}
    </PdfPreloadContext.Provider>
  )
}

export const usePdfPreload = () => {
  const context = useContext(PdfPreloadContext)

  if (!context) {
    throw new Error('PdfPreloadContext is missing in the module!')
  }

  return context
}
